import React, { useState } from 'react';
import './EquipmentProgress.scss';
import { Col, Row } from 'react-bootstrap';
import {
    GetAssestLoadsByLoadId_getAssestLoadsByLoadId as AssetLoadType,
} from '../../../../../types/schemaTypes';
import { PermissionEnum } from '../../../../../types/PermissionEnum';
import { useAuthContext } from '../../../../Auth/AuthProvider';
import EditEquipmentProgressModal from './EditEquipmentProgressModal';
import { useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { EDIT_ASSET_LOAD_PROGRESS } from '../../../../../graphql/client/mutations';
import { toast } from 'react-toastify';
import { toastOpt } from '../../../../../utils/helpers/toastHelper';
import moment from 'moment';
import { GET_ASSET_LOADS } from '../../../../../graphql/client/queries';

interface EquipmentProgressProps {
    loadId: number,
    assetLoad: AssetLoadType;
}

const EquipmentProgress = ({ loadId, assetLoad }: EquipmentProgressProps): React.ReactElement => {
    const authContext = useAuthContext();
    const canEditAssetLoadProgress = authContext.userHasPermission(PermissionEnum.EditAssetLoadTimestamps);

    const [showModal, setShowModal] = useState(false);
    const [modalError, setModalError] = useState('');

    const handleOnClickEdit = (): void => {
        setShowModal(true);
    };

    const handleOnClickHide = (): void => {
        setShowModal(false);
        setModalError('');
    };

    const [editAssetLoadProgress, { loading: mutationLoading }] = useMutation(EDIT_ASSET_LOAD_PROGRESS, {
        onCompleted: () => {
            toast.success('Asset load progress updated successfully', toastOpt);
            handleOnClickHide();
        },
        onError: (error) => {
            if (CombinedGraphQLErrors.is(error))
                setModalError(error.errors[0]?.message?.toString().replace(',', ' ') || error.message);
            else
                setModalError(error.message)
        },
        refetchQueries: [
            {
                query: GET_ASSET_LOADS,
                variables: { loadId: loadId },
            },
        ],
    });

    const handleOnClickSave = (atOriginIn, atOriginOut, atDestinationIn, atDestinationOut, status) => {
        editAssetLoadProgress({
            variables: {
                input: {
                    loadId: loadId,
                    currentValues: {
                        atOriginIn: assetLoad.atOriginIn ? moment(assetLoad.atOriginIn).utc() : undefined,
                        atOriginOut: assetLoad.atOriginOut ? moment(assetLoad.atOriginOut).utc() : undefined,
                        atDestinationIn: assetLoad.atDestinationIn ? moment(assetLoad.atDestinationIn).utc() : undefined,
                        atDestinationOut: assetLoad.atDestinationOut ? moment(assetLoad.atDestinationOut).utc() : undefined,
                        status: assetLoad.status ? assetLoad.status : undefined,
                    },
                    newValues: {
                        atOriginIn: (assetLoad.atOriginIn == atOriginIn) ? undefined : (atOriginIn ? atOriginIn?.utc() : undefined),
                        atOriginOut: (assetLoad.atOriginOut == atOriginOut) ? undefined : (atOriginOut ? atOriginOut?.utc() : undefined),
                        atDestinationIn: (assetLoad.atDestinationIn == atDestinationIn) ? undefined : (atDestinationIn ? atDestinationIn?.utc() : undefined),
                        atDestinationOut: (assetLoad.atDestinationOut == atDestinationOut) ? undefined : (atDestinationOut ? atDestinationOut?.utc() : undefined),
                        status: assetLoad.status == status ? undefined : status,
                    }
                }
            }
        })
    }

    let trackerState = 0;
    if (assetLoad?.status === 'ReadyForDispatch') trackerState = 1;
    if (assetLoad?.status === 'Assigned') trackerState = 2;
    if (assetLoad?.status === 'Loading') trackerState = 3;
    if (assetLoad?.status === 'Loaded') trackerState = 4;
    if (assetLoad?.status === 'Unloading') trackerState = 5;
    if (assetLoad?.status === 'Delivered') trackerState = 6;

    return (
        <div className="EquipmentProgress-Container">
            {canEditAssetLoadProgress && assetLoad?.status !== "Canceled" &&
                <div className='EquipmentProgress-Edit' onClick={handleOnClickEdit}>
                    Edit
                </div>
            }

            <Row className="EquipmentProgress-Label-Container">
                <Col className="EquipmentProgress-Label" />
                <Col className="EquipmentProgress-Label" />
                <Col className="EquipmentProgress-Label" />
                {
                    assetLoad?.miles ? (
                        <Col>
                            <p className="EquipmentProgress-MilesLabel">{assetLoad?.miles} mile(s)</p>
                        </Col>
                    ) : <Col></Col>
                }
                <Col className="EquipmentProgress-Label" />
                <Col className="EquipmentProgress-Label" />
            </Row>

            <div className="step-wizard">
                <div className="step-wizard-row setup-panel">
                    <div className="step-wizard-step">
                        <div className="icon-size">
                            {
                                trackerState >= 1 ? (
                                    <i className="material-icons color EquipmentProgress-Icon">
                                        check_circle_outline
                                    </i>
                                ) : (
                                    <i className="material-icons color EquipmentProgress-NotCompletedIcon ">
                                        brightness_1
                                    </i>
                                )
                            }
                        </div>
                        <div className='Equipment-Text'>
                            <p>Ready for Dispatch</p>
                            <p className="date"> {assetLoad?.readyForDispatchDateTime}</p>
                        </div>
                    </div>

                    <div className="step-wizard-step">
                        <div className="icon-size">
                            {
                                trackerState >= 2 ? (
                                    <i className="material-icons color EquipmentProgress-Icon">
                                        check_circle_outline
                                    </i>
                                ) : trackerState === 1 ? (
                                    <i className="material-icons color EquipmentProgress-OnIcon ">
                                        brightness_1
                                    </i>
                                ) : (
                                    <i className="material-icons color EquipmentProgress-NotCompletedIcon ">
                                        brightness_1
                                    </i>
                                )
                            }
                        </div>
                        <p>Assigned</p>
                        <p className="date"> {assetLoad?.assignedDateTime}</p>
                    </div>

                    <div className="step-wizard-step">
                        <div className="icon-size">
                            {
                                trackerState >= 3 ? (
                                    <i className="material-icons color EquipmentProgress-Icon">
                                        check_circle_outline
                                    </i>
                                ) : trackerState === 2 ? (
                                    <i className="material-icons color EquipmentProgress-OnIcon ">
                                        brightness_1
                                    </i>
                                ) : (
                                    <i className="material-icons color EquipmentProgress-NotCompletedIcon ">
                                        brightness_1
                                    </i>
                                )
                            }
                        </div>
                        <p>Loading</p>
                        <p className="date"> {assetLoad?.atOriginIn}</p>
                    </div>


                    <div className="step-wizard-step">
                        <div className="icon-size">
                            {
                                trackerState >= 4 ? (
                                    <i className="material-icons color EquipmentProgress-Icon">
                                        check_circle_outline
                                    </i>
                                ) : trackerState === 3 ? (
                                    <i className="material-icons color EquipmentProgress-OnIcon ">
                                        brightness_1
                                    </i>
                                ) : (
                                    <i className="material-icons color EquipmentProgress-NotCompletedIcon ">
                                        brightness_1
                                    </i>
                                )
                            }
                        </div>
                        <p>Loaded</p>
                        <p className="date"> {assetLoad?.atOriginOut}</p>
                    </div>

                    <div className="step-wizard-step">
                        <div className="icon-size">
                            {
                                trackerState >= 5 ? (
                                    <i className="material-icons color EquipmentProgress-Icon">
                                        check_circle_outline
                                    </i>
                                ) : trackerState === 4 ? (
                                    <i className="material-icons color EquipmentProgress-OnIcon ">
                                        brightness_1
                                    </i>
                                ) : (
                                    <i className="material-icons color EquipmentProgress-NotCompletedIcon ">
                                        brightness_1
                                    </i>
                                )
                            }
                        </div>
                        <p>Unloading</p>
                        <p className="date"> {assetLoad?.atDestinationIn}</p>
                    </div>

                    <div className="step-wizard-step">
                        <div className="icon-size">
                            {
                                trackerState === 6 ? (
                                    <i className="material-icons color EquipmentProgress-Icon">
                                        check_circle_outline
                                    </i>
                                ) : trackerState === 5 ? (
                                    <i className="material-icons color EquipmentProgress-OnIcon ">
                                        brightness_1
                                    </i>
                                ) : (
                                    <i className="material-icons color EquipmentProgress-NotCompletedIcon ">
                                        brightness_1
                                    </i>
                                )
                            }
                        </div>
                        <p>Delivered</p>
                        <p className="date"> {assetLoad?.atDestinationOut}</p>
                    </div>
                </div>
            </div>

            {
                canEditAssetLoadProgress && showModal && <EditEquipmentProgressModal
                    showModal={showModal}
                    assetLoad={assetLoad}
                    loading={mutationLoading}
                    error={modalError}
                    onClickCancel={handleOnClickHide}
                    onClickSave={handleOnClickSave}
                />
            }
        </div>
    )
};

export default EquipmentProgress;

